import React from 'react';
import './DataTable.css';

export interface DataTableColumn<Row> {
  key: string;
  header: React.ReactNode;
  accessor: (row: Row) => React.ReactNode;
  sortable?: boolean;
  /** Optional comparator for sortable columns. Defaults to string compare. */
  compare?: (a: Row, b: Row) => number;
  width?: string | number;
  align?: 'left' | 'right' | 'center';
}

export interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  /** Unique key extractor. Stable rendering and accessibility. */
  getRowKey: (row: Row, index: number) => string;
  /** Optional page size; set to disable pagination. */
  pageSize?: number;
  /** Alternate-row shading. */
  zebra?: boolean;
  /** Shown when rows is empty. */
  emptyState?: React.ReactNode;
  /** Called when a row is clicked. */
  onRowClick?: (row: Row) => void;
  className?: string;
}

type SortState = { key: string; direction: 'asc' | 'desc' } | null;

export function DataTable<Row>({
  columns,
  rows,
  getRowKey,
  pageSize,
  zebra,
  emptyState,
  onRowClick,
  className
}: DataTableProps<Row>) {
  const [sort, setSort] = React.useState<SortState>(null);
  const [page, setPage] = React.useState(0);

  const sorted = React.useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const compare = col.compare
      || ((a: Row, b: Row) => String(col.accessor(a) ?? '').localeCompare(String(col.accessor(b) ?? '')));
    const sortedRows = [...rows].sort(compare);
    return sort.direction === 'asc' ? sortedRows : sortedRows.reverse();
  }, [rows, sort, columns]);

  const totalPages = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const currentPage = Math.min(page, totalPages - 1);
  const pageRows = pageSize ? sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize) : sorted;

  React.useEffect(() => {
    if (page >= totalPages) setPage(Math.max(0, totalPages - 1));
  }, [page, totalPages]);

  const handleHeaderClick = (col: DataTableColumn<Row>) => {
    if (!col.sortable) return;
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, direction: 'asc' };
      if (prev.direction === 'asc') return { key: col.key, direction: 'desc' };
      return null;
    });
  };

  const classes = [
    'bso-data-table',
    zebra ? 'bso-data-table--zebra' : '',
    className ?? ''
  ].filter(Boolean).join(' ');

  return (
    <div>
      <table className={classes}>
        <thead className="bso-data-table__thead">
          <tr>
            {columns.map((col) => {
              const isActive = sort?.key === col.key;
              const thClasses = [
                'bso-data-table__th',
                col.sortable ? 'bso-data-table__th--sortable' : ''
              ].filter(Boolean).join(' ');
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={thClasses}
                  onClick={col.sortable ? () => handleHeaderClick(col) : undefined}
                  style={{
                    width: col.width,
                    textAlign: col.align
                  }}
                  aria-sort={
                    col.sortable
                      ? isActive
                        ? sort!.direction === 'asc' ? 'ascending' : 'descending'
                        : 'none'
                      : undefined
                  }
                >
                  {col.header}
                  {col.sortable && isActive && (
                    <span className="bso-data-table__sort-indicator" aria-hidden="true">
                      {sort!.direction === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="bso-data-table__tbody">
          {pageRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="bso-data-table__empty">
                {emptyState ?? 'No rows to display.'}
              </td>
            </tr>
          ) : (
            pageRows.map((row, i) => (
              <tr
                key={getRowKey(row, i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={{ cursor: onRowClick ? 'pointer' : undefined }}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className="bso-data-table__td"
                    style={{ textAlign: col.align }}
                  >
                    {col.accessor(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {pageSize && sorted.length > pageSize && (
        <div className="bso-data-table__footer">
          <div>
            Showing {currentPage * pageSize + 1}–
            {Math.min((currentPage + 1) * pageSize, sorted.length)} of {sorted.length}
          </div>
          <div className="bso-data-table__pagination">
            <button
              type="button"
              className="bso-data-table__page-btn"
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              Previous
            </button>
            <span>
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              type="button"
              className="bso-data-table__page-btn"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
